/**
 * MockAuthProvider
 * Mock implementation for development and testing
 */

import { nanoid } from 'nanoid'
import type { IAuthProvider } from '@/core/interfaces'
import type { User, LoginDto, SignupDto, AuthSession, UpdateUserDto } from '@/core/models'

// Mock users database
const mockUsers: User[] = [
  {
    id: 'user-1',
    email: 'demo@ebookgenius.com',
    name: 'Demo User',
    createdAt: new Date('2024-01-01'),
    lastLoginAt: new Date(),
  },
]

// Mock passwords (in real app, these would be hashed)
const mockPasswords: Record<string, string> = {
  'user-1': 'password123',
}

export class MockAuthProvider implements IAuthProvider {
  private currentSession: AuthSession | null = null

  async login(credentials: LoginDto): Promise<AuthSession> {
    // Simulate network delay
    await new Promise(resolve => setTimeout(resolve, 500))

    const user = mockUsers.find(u => u.email === credentials.email)
    if (!user) {
      throw new Error('Invalid email or password')
    }

    const password = mockPasswords[user.id]
    if (password !== credentials.password) {
      throw new Error('Invalid email or password')
    }

    // Update last login
    user.lastLoginAt = new Date()

    const session: AuthSession = {
      user,
      token: `mock-token-${nanoid()}`,
      expiresAt: new Date(Date.now() + 24 * 60 * 60 * 1000), // 24 hours
    }

    this.currentSession = session
    this.persistSession(session)

    return session
  }

  async signup(data: SignupDto): Promise<AuthSession> {
    // Simulate network delay
    await new Promise(resolve => setTimeout(resolve, 500))

    // Check if email already exists
    if (mockUsers.find(u => u.email === data.email)) {
      throw new Error('Email already in use')
    }

    const now = new Date()
    const user: User = {
      id: `user-${nanoid()}`,
      email: data.email,
      name: data.name,
      createdAt: now,
      lastLoginAt: now,
    }

    mockUsers.push(user)
    mockPasswords[user.id] = data.password

    const session: AuthSession = {
      user,
      token: `mock-token-${nanoid()}`,
      expiresAt: new Date(Date.now() + 24 * 60 * 60 * 1000),
    }

    this.currentSession = session
    this.persistSession(session)

    return session
  }

  async logout(): Promise<void> {
    this.currentSession = null
    this.clearSession()
  }

  async getCurrentUser(): Promise<User | null> {
    if (!this.currentSession) {
      this.loadSession()
    }
    return this.currentSession?.user || null
  }

  async isAuthenticated(): Promise<boolean> {
    if (!this.currentSession) {
      this.loadSession()
    }

    if (!this.currentSession) {
      return false
    }

    // Check if token is expired
    if (new Date() > new Date(this.currentSession.expiresAt)) {
      await this.logout()
      return false
    }

    return true
  }

  async refreshToken(): Promise<AuthSession> {
    if (!this.currentSession) {
      throw new Error('No active session')
    }

    const session: AuthSession = {
      ...this.currentSession,
      token: `mock-token-${nanoid()}`,
      expiresAt: new Date(Date.now() + 24 * 60 * 60 * 1000),
    }

    this.currentSession = session
    this.persistSession(session)

    return session
  }

  async updateUser(userId: string, updates: UpdateUserDto): Promise<User> {
    const user = mockUsers.find(u => u.id === userId)
    if (!user) {
      throw new Error('User not found')
    }

    if (updates.name) user.name = updates.name
    if (updates.email) user.email = updates.email
    if (updates.avatar) user.avatar = updates.avatar

    if (this.currentSession && this.currentSession.user.id === userId) {
      this.currentSession.user = { ...user }
      this.persistSession(this.currentSession)
    }

    return { ...user }
  }

  async changePassword(
    userId: string,
    oldPassword: string,
    newPassword: string,
  ): Promise<void> {
    const currentPassword = mockPasswords[userId]
    if (!currentPassword) {
      throw new Error('User not found')
    }

    if (currentPassword !== oldPassword) {
      throw new Error('Current password is incorrect')
    }

    mockPasswords[userId] = newPassword
  }

  async requestPasswordReset(email: string): Promise<void> {
    // Simulate network delay
    await new Promise(resolve => setTimeout(resolve, 500))

    // In mock, we just pretend to send email
    const user = mockUsers.find(u => u.email === email)
    if (!user) {
      // For security, don't reveal if email exists
      return
    }

    console.log(`[Mock] Password reset email sent to ${email}`)
  }

  async resetPassword(token: string, _newPassword: string): Promise<void> {
    // In mock implementation, just accept any token
    await new Promise(resolve => setTimeout(resolve, 500))
    console.log(`[Mock] Password reset for token ${token}`)
  }

  // Helper methods for session persistence
  private persistSession(session: AuthSession): void {
    try {
      localStorage.setItem('auth_session', JSON.stringify(session))
    } catch (error) {
      console.error('Failed to persist session:', error)
    }
  }

  private loadSession(): void {
    try {
      const stored = localStorage.getItem('auth_session')
      if (stored) {
        this.currentSession = JSON.parse(stored, (key, value) => {
          // Parse dates
          if (key === 'createdAt' || key === 'lastLoginAt' || key === 'expiresAt') {
            return new Date(value)
          }
          return value
        })
      }
    } catch (error) {
      console.error('Failed to load session:', error)
    }
  }

  private clearSession(): void {
    try {
      localStorage.removeItem('auth_session')
    } catch (error) {
      console.error('Failed to clear session:', error)
    }
  }
}
